import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createBlock as _createBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue";
import _imports_0 from '@/assets/arrow-right.png';
const _hoisted_1 = {
    key: 0,
    class: "policy-page"
};
const _hoisted_2 = { class: "policy-card-container" };
const _hoisted_3 = { class: "summary-content" };
const _hoisted_4 = {
    key: 2,
    class: "policy-page"
};
const _hoisted_5 = { class: "policy-card-container" };
const _hoisted_6 = { class: "summary-content" };
const _hoisted_7 = { class: "section-title" };
const _hoisted_8 = { class: "fields-list" };
const _hoisted_9 = { class: "field-label" };
const _hoisted_10 = { class: "field-value" };
const _hoisted_11 = {
    key: 1,
    class: "last-updated-row"
};
const _hoisted_12 = { class: "last-updated" };
const _hoisted_13 = { class: "edit-sections" };
const _hoisted_14 = {
    key: 0,
    class: "section-title"
};
const _hoisted_15 = { class: "anchor-bottom-no-nav" };
const _hoisted_16 = {
    key: 0,
    class: "docsPanel"
};
const _hoisted_17 = ["href"];
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useCustomer } from '@/composables/useCustomer';
import { useContent } from '@/composables/useContent';
import { useAssetConfig } from '@/composables/useAssetConfig';
import { useDocuments } from '@/composables/useDocuments';
import HeaderBlock from './elements/HeaderBlock.vue';
import DrawerPanel from './elements/DrawerPanel.vue';
import AccountListItem from './elements/AccountListItem.vue';
import ProgressDial from './elements/ProgressDial.vue';
import InputSections from './elements/InputSections.vue';
import MGButton from './elements/MGButton.vue';
import MGDialog from './elements/MGDialog.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'AssetPage',
    setup(__props) {
        const route = useRoute();
        const router = useRouter();
        const state = useAppState();
        useAuth();
        const { getCustomer } = useCustomer();
        const { getAiopSection } = useContent();
        const { getConfig } = useAssetConfig();
        const { getDocumentsByHubId } = useDocuments();
        const config = getConfig(route.meta.assetType);
        const instance = getCurrentInstance();
        const cookies = instance?.appContext.config.globalProperties.$cookies;
        const editMode = ref(false);
        const revalidating = ref(false);
        const showDeleteConfirm = ref(false);
        const deleting = ref(false);
        const editSectionsRaw = ref([]);
        const sectionsReady = ref(false);
        const dropdownsLoaded = ref(false);
        const formatter = computed(() => state.formatter);
        const account = computed(() => config.getStateAccount());
        // Card reflects the saved account (updates on save, which refetches the account).
        const cardData = computed(() => account.value ? config.card(account.value, state.formatter) : null);
        const dial = computed(() => account.value ? config.progressDial(account.value) : null);
        // Field labels currently active per the server-JSON rules (same set edit uses).
        const activeLabels = computed(() => new Set(editSections.value.flatMap((s) => s.fields.map((f) => f.label))));
        const viewSections = computed(() => {
            if (!account.value)
                return [];
            return config.viewSections(account.value, state.formatter)
                .map(s => ({
                title: s.title,
                fields: s.fields.filter(f => {
                    if (f.value == null || f.value === '')
                        return false;
                    // Rule-gated fields: only show once the JSON says they apply.
                    if (f.requires && !activeLabels.value.has(f.requires))
                        return false;
                    return true;
                }),
            }))
                .filter(s => s.fields.length > 0);
        });
        const headerActionTop = computed(() => {
            const safeTop = parseInt(cookies?.get('st') ?? 0);
            return 16 + safeTop;
        });
        // Edit sections, with rule-gated sections shown reactively off the field the
        // rule keys on (same rule logic as the add wizards).
        const editSections = computed(() => {
            return editSectionsRaw.value.filter((section) => {
                if (!section.rule)
                    return true;
                const [ruleKey, ruleValue] = section.rule.split(':').map((s) => s.trim());
                const ruleValues = ruleValue.split(',').map((s) => s.trim());
                return ruleValues.includes(findFieldValueById(ruleKey));
            });
        });
        // Regroup the active (rule-filtered) edit fields into the view's headed
        // sections + order. Fields bind to the original reactive objects, so save
        // logic is unchanged. Any field not placed by config falls through to an
        // untitled trailing group so nothing is silently dropped.
        const editGroups = computed(() => {
            const active = editSections.value.flatMap((s) => s.fields);
            const used = new Set();
            const groups = config.editGroups
                .map(g => {
                const fields = g.labels
                    .map(label => active.find((f) => f.label === label))
                    .filter(Boolean);
                fields.forEach((f) => used.add(f));
                return { title: g.title, fields };
            })
                .filter(g => g.fields.length > 0);
            const leftover = active.filter((f) => !used.has(f));
            if (leftover.length)
                groups.push({ title: '', fields: leftover });
            return groups;
        });
        // --- Helpers (label-based, shared with the add wizards) ---
        function formatDate(dateString) {
            if (!dateString)
                return '';
            const date = new Date(dateString);
            if (isNaN(date.getTime()))
                return dateString;
            return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        }
        function findField(fieldName) {
            for (const section of editSectionsRaw.value) {
                for (const field of section.fields) {
                    if (field.label === fieldName)
                        return field;
                }
            }
            return null;
        }
        function findFieldValue(fieldName) {
            for (const section of editSectionsRaw.value) {
                for (const field of section.fields) {
                    if (field.label === fieldName && field.value != null)
                        return field.value;
                }
            }
            return null;
        }
        function findFieldValueById(fieldId) {
            for (const section of editSectionsRaw.value) {
                for (const field of section.fields) {
                    if (field.id === fieldId && field.value != null)
                        return field.value;
                }
            }
            return null;
        }
        function loadFieldsFromObject(fieldMappings) {
            Object.keys(fieldMappings).forEach(fieldName => {
                editSectionsRaw.value.forEach(section => {
                    section.fields.forEach((field) => {
                        if (field.label === fieldName)
                            field.value = fieldMappings[fieldName];
                    });
                });
            });
        }
        function removeNullProperties(obj) {
            Object.keys(obj).forEach(key => obj[key] == null && delete obj[key]);
            return obj;
        }
        // --- Sections (shared by view-gating and edit) ---
        // Load the server JSON and populate field VALUES from the account so the rule
        // engine (activeLabels / editSections) can decide visibility. No provider/type
        // dropdown fetches here — those are only needed once the user enters edit.
        function loadSections(then = null) {
            getAiopSection(config.sectionJson, () => {
                editSectionsRaw.value = state.sectionJson;
                // Guard: populate now runs on every view load, so a quirky account shape
                // must not break the view (or trip getAiopSection's local-fallback catch).
                try {
                    if (account.value)
                        loadFieldsFromObject(config.populate(account.value));
                }
                catch (e) { /* gated fields stay hidden */ }
                sectionsReady.value = true;
                if (then)
                    then();
            });
        }
        // --- Edit flow ---
        function loadEditSections() {
            const enter = () => {
                if (!dropdownsLoaded.value) {
                    config.populateDropdowns(findField);
                    dropdownsLoaded.value = true;
                }
                clearFieldErrors();
                editMode.value = true;
            };
            if (sectionsReady.value)
                enter();
            else
                loadSections(enter);
        }
        function cancelEdit() {
            editMode.value = false;
            clearFieldErrors();
            // Discard any uncommitted edits so the view's rule-gating reflects the
            // saved account again (editSectionsRaw is shared with the rule engine).
            if (account.value)
                loadFieldsFromObject(config.populate(account.value));
        }
        function clearFieldErrors() {
            editSectionsRaw.value.forEach((s) => s.fields.forEach((f) => { f.errorText = null; }));
        }
        // Mirror the add wizards' required-field validation before saving. Flags each
        // invalid field inline (InputSections shows field.errorText under the field).
        function validateEdit() {
            clearFieldErrors();
            let valid = true;
            for (const section of editSections.value) {
                for (const field of section.fields) {
                    if (field.required && (field.value == null || field.value === '')) {
                        field.errorText = field.validationText || `${field.label} is required`;
                        valid = false;
                    }
                    else if (field.type === 'datetext' && field.value && field.value.length < 10) {
                        field.errorText = 'Please enter date in the format dd/mm/yyyy';
                        valid = false;
                    }
                }
            }
            return valid;
        }
        function saveChanges() {
            if (!validateEdit())
                return;
            const payload = config.build(findFieldValue);
            payload.id = route.params.id;
            removeNullProperties(payload);
            config.updateAccount(payload, (res) => {
                if (res) {
                    // The single-account GET hits a server /Cached endpoint that lags the
                    // write. SWR+poll until it reflects the save; show a skeleton (rather
                    // than the stale value) until the fresh result lands.
                    editMode.value = false;
                    revalidating.value = true;
                    config.getAccount(route.params.id, (_result, isStale) => {
                        if (!isStale)
                            revalidating.value = false;
                    }, true, true, true, 2000);
                    config.revalidate();
                    // Safety net: never leave the skeleton stuck if the poll gives up.
                    setTimeout(() => { revalidating.value = false; }, 10000);
                }
            });
        }
        // --- Delete flow ---
        function handleHeaderDelete(done) {
            config.deleteAccount(route.params.id, () => {
                config.revalidate();
                if (done)
                    done();
                router.push('/money');
            });
        }
        function confirmDelete() {
            deleting.value = true;
            config.deleteAccount(route.params.id, () => {
                config.revalidate();
                router.push('/money');
            });
        }
        onMounted(() => {
            getCustomer();
            // Clear any documents left in shared state by another page so the drawer
            // never flashes stale docs before this account's fetch returns.
            state.documents = null;
            config.getAccount(route.params.id, () => {
                loadSections();
                // Load any documents linked to this account for the "Saved documents" drawer
                // (only the types whose old pages had it — not mortgage).
                if (config.hasDocuments && account.value)
                    getDocumentsByHubId(account.value.id);
            });
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_col = _resolveComponent("v-col");
            const _component_v_row = _resolveComponent("v-row");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, {
                    title: _unref(config).title,
                    border: "0",
                    edit: !editMode.value,
                    del: !editMode.value,
                    onEdit: loadEditSections,
                    onDelete: handleHeaderDelete,
                    menuDescription: _unref(config).menuDescription
                }, null, 8, ["title", "edit", "del", "menuDescription"]),
                (!account.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                        _createElementVNode("div", _hoisted_2, [
                            _createVNode(_component_v_skeleton_loader, { type: "list-item-avatar-two-line" })
                        ]),
                        _createElementVNode("div", _hoisted_3, [
                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (n) => {
                                return _createElementVNode("div", {
                                    class: "section-group",
                                    key: n
                                }, [
                                    _createVNode(_component_v_skeleton_loader, { type: "heading" }),
                                    _createVNode(_component_v_skeleton_loader, { type: "list-item, list-item, list-item" })
                                ]);
                            }), 64))
                        ])
                    ]))
                    : _createCommentVNode("", true),
                (account.value && editMode.value)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        class: "header-action-btn",
                        style: _normalizeStyle('top: ' + headerActionTop.value + 'px;'),
                        onClick: _cache[0] || (_cache[0] = ($event) => (cancelEdit()))
                    }, [
                        _createVNode(_component_v_icon, {
                            icon: "mdi-close",
                            size: "20"
                        })
                    ], 4))
                    : _createCommentVNode("", true),
                (account.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                            (revalidating.value)
                                ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                                    key: 0,
                                    type: "list-item-avatar-two-line"
                                }))
                                : (_openBlock(), _createBlock(AccountListItem, {
                                    key: 1,
                                    "provider-name": cardData.value.providerName,
                                    line1: cardData.value.line1,
                                    line2: cardData.value.line2,
                                    label: cardData.value.label,
                                    value: cardData.value.value,
                                    "avatar-url": cardData.value.avatarUrl
                                }, null, 8, ["provider-name", "line1", "line2", "label", "value", "avatar-url"]))
                        ]),
                        _createElementVNode("div", _hoisted_6, [
                            (!editMode.value)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    (dial.value && (!dial.value.requires || activeLabels.value.has(dial.value.requires)))
                                        ? (_openBlock(), _createBlock(ProgressDial, {
                                            key: 0,
                                            fromDate: dial.value.fromDate,
                                            toDate: dial.value.toDate,
                                            title: dial.value.title,
                                            footerLabel: dial.value.footerLabel,
                                            messages: dial.value.messages
                                        }, null, 8, ["fromDate", "toDate", "title", "footerLabel", "messages"]))
                                        : _createCommentVNode("", true),
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(viewSections.value, (section) => {
                                        return (_openBlock(), _createElementBlock("div", {
                                            class: "section-group",
                                            key: section.title
                                        }, [
                                            _createElementVNode("h2", _hoisted_7, _toDisplayString(section.title), 1),
                                            _createElementVNode("div", _hoisted_8, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.fields, (field) => {
                                                    return (_openBlock(), _createElementBlock("div", {
                                                        class: _normalizeClass(["field-card", { 'field-card-block': field.block }]),
                                                        key: field.label
                                                    }, [
                                                        _createElementVNode("span", _hoisted_9, _toDisplayString(field.label), 1),
                                                        _createElementVNode("span", _hoisted_10, _toDisplayString(field.value), 1)
                                                    ], 2));
                                                }), 128))
                                            ])
                                        ]));
                                    }), 128)),
                                    _createVNode(MGButton, {
                                        text: _unref(config).deleteLabel,
                                        fullWidth: true,
                                        arrow: true,
                                        onClick: _cache[1] || (_cache[1] = ($event) => (showDeleteConfirm.value = true))
                                    }, null, 8, ["text"]),
                                    (account.value.lastUpdatedDateTime)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                            _createElementVNode("p", _hoisted_12, "Last updated: " + _toDisplayString(formatDate(account.value.lastUpdatedDateTime)) + ".", 1)
                                        ]))
                                        : _createCommentVNode("", true)
                                ], 64))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createElementVNode("div", _hoisted_13, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(editGroups.value, (group) => {
                                            return (_openBlock(), _createElementBlock("div", {
                                                class: "section-group",
                                                key: group.title || 'extra'
                                            }, [
                                                (group.title)
                                                    ? (_openBlock(), _createElementBlock("h2", _hoisted_14, _toDisplayString(group.title), 1))
                                                    : _createCommentVNode("", true),
                                                _createVNode(InputSections, {
                                                    sections: [{ fields: group.fields }],
                                                    formatter: formatter.value,
                                                    theme: "light",
                                                    editMode: true,
                                                    outline: false
                                                }, null, 8, ["sections", "formatter"])
                                            ]));
                                        }), 128))
                                    ]),
                                    _createElementVNode("div", _hoisted_15, [
                                        _createVNode(MGButton, {
                                            text: "Save changes",
                                            fullWidth: true,
                                            onClick: saveChanges
                                        })
                                    ])
                                ], 64))
                        ])
                    ]))
                    : _createCommentVNode("", true),
                _createVNode(MGDialog, {
                    modelValue: showDeleteConfirm.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((showDeleteConfirm).value = $event)),
                    title: _unref(config).deleteLabel,
                    maxWidth: "500px",
                    contentHeight: "auto",
                    persistent: false
                }, {
                    content: _withCtx(() => _cache[4] || (_cache[4] = [
                        _createElementVNode("p", null, "Are you sure you want to delete this? This action cannot be undone.", -1)
                    ])),
                    actions: _withCtx(() => [
                        _createVNode(MGButton, {
                            fullWidth: true,
                            onClick: confirmDelete,
                            text: deleting.value ? 'Deleting...' : 'Delete',
                            disabled: deleting.value
                        }, null, 8, ["text", "disabled"]),
                        _createVNode(MGButton, {
                            stacked: true,
                            fullWidth: true,
                            onClick: _cache[2] || (_cache[2] = ($event) => (showDeleteConfirm.value = false)),
                            outline: true,
                            text: "Cancel",
                            disabled: deleting.value
                        }, null, 8, ["disabled"])
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"]),
                (_unref(config).hasDocuments && account.value && _unref(state).documents && _unref(state).documents.length > 0)
                    ? (_openBlock(), _createBlock(DrawerPanel, {
                        key: 3,
                        bottom: "-18px",
                        openHeight: "450px",
                        title: "Saved documents",
                        backgroundColor: "white",
                        openBackgroundColor: "white"
                    }, {
                        default: _withCtx(() => [
                            (_unref(state).documents)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(state).documents, (d) => {
                                        return (_openBlock(), _createElementBlock("a", {
                                            key: d.id,
                                            href: d.url
                                        }, [
                                            _createVNode(_component_v_row, null, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_col, { cols: "1" }, {
                                                        default: _withCtx(() => [
                                                            _createVNode(_component_v_icon, {
                                                                class: "docIcon",
                                                                icon: "mdi-file-document-outline"
                                                            })
                                                        ]),
                                                        _: 1
                                                    }),
                                                    _createVNode(_component_v_col, { class: "docCol" }, {
                                                        default: _withCtx(() => [
                                                            _createTextVNode(_toDisplayString(d.name), 1),
                                                            _cache[5] || (_cache[5] = _createElementVNode("br", null, null, -1)),
                                                            _cache[6] || (_cache[6] = _createElementVNode("span", null, null, -1))
                                                        ]),
                                                        _: 2
                                                    }, 1024),
                                                    _createVNode(_component_v_col, {
                                                        cols: "1",
                                                        class: "arrowCol"
                                                    }, {
                                                        default: _withCtx(() => _cache[7] || (_cache[7] = [
                                                            _createElementVNode("img", {
                                                                class: "arrow",
                                                                src: _imports_0
                                                            }, null, -1)
                                                        ])),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 2
                                            }, 1024)
                                        ], 8, _hoisted_17));
                                    }), 128))
                                ]))
                                : _createCommentVNode("", true)
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true)
            ], 64));
        };
    }
});
